import { useEffect } from 'react';
import { sentryCaptureException } from '@/utils/sentry';
import { useAddonPurchase } from '../hooks/use-addon-purchase';
import { addonRequestSchema } from '../utils/addon-purchase';

/**
 * Window bridge for add-on purchases.
 *
 * Exposes `window.__cio_addons` so the embedded Ember app can open the add-on
 * purchase modal, which only renders inside Hydra's app layout.
 */
interface CioAddonsBridge {
  /** `request` comes from another repo that deploys on its own, so it's checked before use. */
  open: (request: unknown) => Promise<{ isPurchased: boolean }>;
}

declare global {
  interface Window {
    __cio_addons?: CioAddonsBridge;
  }
}

export function AddonPurchaseBridge() {
  const { openAddon } = useAddonPurchase();

  useEffect(() => {
    window.__cio_addons = {
      open: async (request) => {
        const parsed = addonRequestSchema.safeParse(request);
        if (!parsed.success) {
          sentryCaptureException(new Error('Refused a malformed add-on request from an embedded app'), {
            extra: { request, issues: parsed.error.issues },
          });
          return { isPurchased: false };
        }

        const result = await openAddon({ request: parsed.data });
        return { isPurchased: result?.isPurchased === true };
      },
    };

    return () => {
      delete window.__cio_addons;
    };
  }, [openAddon]);

  return null;
}
